import {useEffect, useRef} from 'react';

import {isIOSDevice} from 'utils/functions';

interface AutoFocusReturn {
  refCallback: (node: HTMLInputElement | null) => void;
  setBlurred: () => void;
}

const useAutoFocus = (autoFocus?: boolean): AutoFocusReturn => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasBlurredRef = useRef(false);
  const isIos = isIOSDevice();

  const adjustScrollAndFocus = () => {
    if (!inputRef.current || hasBlurredRef.current) return;
    
    const rect = inputRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const keyboardHeight = window.visualViewport
      ? windowHeight - window.visualViewport.height
      : 0;
    const visibleHeight = windowHeight - keyboardHeight;
    const elementHeight = rect.height;
    const elementTop = rect.top + window.scrollY;

    const scrollPosition = elementTop - (visibleHeight - elementHeight - 20);

    window.scrollTo({
      top: scrollPosition > 0 ? scrollPosition : 0,
      behavior: 'smooth',
    });

    if (!isIos) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    if (autoFocus && inputRef.current && !hasBlurredRef.current) {
      const addViewportListener = () => {
        if (window.visualViewport && !hasBlurredRef.current) {
          const visualViewport = window.visualViewport;
          visualViewport.addEventListener('resize', adjustScrollAndFocus);
          return () => {
            visualViewport.removeEventListener('resize', adjustScrollAndFocus);
          };
        }
        return undefined;
      };

      if (isIos) {
        inputRef.current.focus();
        const iosTimeoutId = setTimeout(() => {
          adjustScrollAndFocus();
          addViewportListener();
        }, 300);

        return () => clearTimeout(iosTimeoutId);
      } else {
        const timeoutId = setTimeout(() => {
          adjustScrollAndFocus();
          addViewportListener();
        }, 300);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [autoFocus]);

  const refCallback = (node: HTMLInputElement | null) => {
    inputRef.current = node; // Присваиваем значение внутри коллбэка
  };

  return {
    refCallback,
    setBlurred: () => {
      hasBlurredRef.current = true;
    },
  };
};

export default useAutoFocus;